import { useMemo } from "react";
import { toast } from "react-toastify";
import { useTour } from "@reactour/tour";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import type {
	ConfigReferencia,
	Factor,
	ImputacionDetalle,
} from "@/@types/Api_Extracted";
import { useAppForm, useSelector } from "@/hooks/useForm";
import {
	Table,
	TableHeader,
	TableRow,
	TableHead,
	TableBody,
	ErrorRow,
	SkeletonRows,
	NoResultsRow,
} from "@/components/ui/table";
import { InputRowMemo } from "./InputRow";

export function InputTable(props: {
	readonly imputacion: UseQueryResult<ImputacionDetalle>;
}) {
	const { isOpen, setIsOpen } = useTour();
	const params = useParams();

	const api = useApi();
	const navigate = useNavigate();

	const queryConfig = api.useQuery("get", "/v1/Empresa/Config/");
	const queryImputacion = props.imputacion;
	const mutateImputacion = api.useMutation(
		"post",
		"/v1/Empresa/Imputaciones/{idImputacion}",
	);

	const isLoading = queryImputacion.isPending || queryConfig.isPending;
	const isError = queryImputacion.isError || queryConfig.isError;

	const defaultValues: ExtendedInputacion = useMemo(() => {
		const tzLike = Temporal.Now.timeZoneId();
		if (queryImputacion.data == null || queryConfig.data == null) {
			return {
				rango: {
					from: undefined,
					to: undefined,
				},
				lineas: [],
			};
		}

		const rango = {
			from: Temporal.Instant.from(queryImputacion.data.desde)
				.toZonedDateTimeISO(tzLike)
				.toPlainDate(),
			to: Temporal.Instant.from(queryImputacion.data.hasta)
				.toZonedDateTimeISO(tzLike)
				.toPlainDate(),
		};

		const lineas = getTableRows(queryImputacion.data.lineas, queryConfig.data);
		return {
			rango: rango,
			lineas: lineas,
		};
	}, [queryImputacion.data, queryConfig.data]);

	const myForm = useAppForm({
		defaultValues: defaultValues,
		onSubmit: async ({ value }) => {
			const parsedLineas = value.lineas
				.filter((l) => l.cantidad != 0 && l.cantidad != null)
				.map(mapLinea);

			if (queryImputacion.data == null) {
				toast("Error al guardar la configuración: imputación no encontrada.", {
					type: "error",
				});
				return;
			}

			try {
				await mutateImputacion.mutateAsync({
					body: {
						idImputacion: queryImputacion.data.idImputacion,
						desde: (value.rango.from?.toString() ?? "") + "T00:00:00.000Z",
						hasta: (value.rango.to?.toString() ?? "") + "T00:00:00.000Z",
						lineas: parsedLineas,
					},
					params: {
						path: { idImputacion: params.inputId ?? "" },
					},
				});
				toast("Guardado.", { type: "success" });
				await queryImputacion.refetch();
				await queryConfig.refetch();
				void navigate("/user");
			} catch (error) {
				console.error("Error al guardar la configuración:", error);
				toast("Error al guardar la configuración.", { type: "error" });
			}
		},
	});

	const formData = useSelector(myForm.store, (state) => state.values.lineas);
	const fields = myForm.state.isPristine ? defaultValues.lineas : formData;

	const sortedFields = useMemo(() => {
		return fields
			.map((row, index) => ({ row, index }))
			.sort((a, b) => {
				const co2A =
					(a.row.cantidad ?? 0) *
					(a.row.factor?.conversion_equivalente_kilos ?? 0);
				const co2B =
					(b.row.cantidad ?? 0) *
					(b.row.factor?.conversion_equivalente_kilos ?? 0);

				return co2B - co2A; // desc
			});
	}, [fields]);

	const isSuccess = !isLoading && !isError;
	const isEmpty = isSuccess && fields.length === 0;

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				// Wait for error handlers, blur events, etc. to finish
				if (isOpen) {
					setIsOpen(false); // Avanza al siguiente paso del tour
				} else {
					setTimeout(() => void myForm.handleSubmit(), 10);
				}
			}}
		>
			<myForm.AppField
				name="rango"
				children={(field) => (
					<field.DateRangeField
						label="Rango de la imputación"
						data-tour-id="date-range"
					/>
				)}
			/>

			<Table className="w-full">
				<TableHeader>
					<TableRow>
						<TableHead data-tour-id="header-factor">Factor</TableHead>
						<TableHead data-tour-id="header-referencia">Referencia</TableHead>
						<TableHead data-tour-id="header-valor">Valor</TableHead>
						<TableHead data-tour-id="header-co2">Co2 equivalente</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody className="text-center">
					{isError && (
						<ErrorRow message="Error al cargar la configuración." colSpan={4} />
					)}
					{isLoading && <SkeletonRows rows={10} colSpan={4} />}
					{isEmpty && (
						<NoResultsRow
							message="No se ha creado la configuración"
							colSpan={4}
						/>
					)}

					{isSuccess &&
						sortedFields.map(({ row, index }) => (
							<InputRowMemo
								key={row.idReferencia}
								row={row}
								index={index}
								form={myForm}
							/>
						))}
				</TableBody>
			</Table>

			<div className="float-right mt-4 flex gap-2">
				<Button
					variant="outline"
					type="button"
					onClick={() => void navigate("/user")}
				>
					Cancelar
				</Button>
				<Button variant="primary" type="submit" data-tour-id="submit-button">
					Guardar
				</Button>
			</div>
		</form>
	);
}

export interface ExtendedInputacion {
	rango: {
		from: Temporal.PlainDate | undefined;
		to: Temporal.PlainDate | undefined;
	};
	lineas: Array<{
		idReferencia: string;
		descripcionReferencia: string;
		cantidad: number | null;
		factor: Factor | null;
	}>;
}

function getTableRows(
	rowsImputacion: ImputacionDetalle["lineas"],
	rowsConfig: ConfigReferencia[],
): ExtendedInputacion["lineas"] {
	return rowsConfig.map((rowConfig) => {
		const rowImputacion = rowsImputacion.find(
			(row) => row.idReferencia === rowConfig.idReferencia,
		);
		return {
			idReferencia: rowConfig.idReferencia,
			descripcionReferencia: rowConfig.descripcionCorta,
			cantidad: rowImputacion?.cantidad ?? 0,
			factor: rowConfig.factor,
		};
	});
}
function mapLinea(linea: ExtendedInputacion["lineas"][number]) {
	if (linea.factor == null) {
		throw new Error("Linea de imputación sin factor");
	}

	const tipo: string = linea.factor.tipoFactor;
	if (tipo === "miteco") {
		return {
			idFactorMiteco: Number(linea.factor.idFactor),
			idBloque: Number(linea.factor.idBloque as number | string),
			idFactorEmpresa: undefined,
			idReferencia: linea.idReferencia,
			descripcionReferencia: linea.descripcionReferencia,
			cantidad: parseFloat(String(linea.cantidad ?? 0)),
		};
	} else if (tipo === "propio") {
		return {
			idFactorMiteco: undefined,
			idFactorEmpresa: String(linea.factor.idFactor),
			idReferencia: linea.idReferencia,
			descripcionReferencia: linea.descripcionReferencia,
			cantidad: parseFloat(String(linea.cantidad ?? 0)),
		};
	} else {
		throw new Error(
			"Linea de imputación no tiene ni idFactorMiteco ni idFactorEmpresa",
		);
	}
}


export interface UseQueryResult<T> {
	isPending: boolean;
	isError: boolean;
	data?: T;
	error?: unknown;
	refetch: () => Promise<unknown>;
}